import React, { useEffect, useState } from 'react';
import { Alert, Card, Spinner } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import messageBoardService, {
  type MessageBoardPostAudit,
} from '../../../services/messageBoardService';
import { MESSAGE_BOARD_PAGE_SIZE, type MessageBoardFeed } from '../hooks/useMessageBoardFeed';
import { formatMessageBoardTime, messageBoardTitle } from '../utils/formatMessageBoardTime';

export interface MessageBoardAuditProps {
  feed: MessageBoardFeed;
  refreshToken?: number;
}

const MessageBoardAudit: React.FC<MessageBoardAuditProps> = ({ feed, refreshToken = 0 }) => {
  const { hasPermission } = usePermissions();
  const canModerate = hasPermission('message_board:moderate');
  const { selectedAreaId, selectedAreaName } = feed;
  const [posts, setPosts] = useState<MessageBoardPostAudit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!canModerate || selectedAreaId == null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    messageBoardService
      .listRemoved(selectedAreaId)
      .then((next) => {
        if (!cancelled) setPosts(next.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPosts(null);
        setError(err instanceof Error ? err.message : 'Failed to load removed messages');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canModerate, selectedAreaId, refreshToken]);

  useEffect(() => {
    setPage(1);
  }, [selectedAreaId]);

  if (!canModerate || selectedAreaId == null) return null;

  const totalPages = Math.max(1, Math.ceil((posts?.length ?? 0) / MESSAGE_BOARD_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visiblePosts =
    posts?.slice((currentPage - 1) * MESSAGE_BOARD_PAGE_SIZE, currentPage * MESSAGE_BOARD_PAGE_SIZE) ??
    [];

  return (
    <Card className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl">
      <Card.Header className="tw-shrink-0 tw-border-b-0 tw-bg-surface tw-px-4 tw-py-3">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content-muted">
          Deleted Messages
        </p>
        <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">
          Taken down in {selectedAreaName}. Read history is kept.
        </p>
      </Card.Header>
      {error ? (
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      ) : null}
      {loading && !posts ? (
        <Card.Body className="tw-flex tw-items-center tw-gap-2 tw-text-content-secondary">
          <Spinner size="sm" />
          <span>Loading removed messages…</span>
        </Card.Body>
      ) : !posts || posts.length === 0 ? (
        <Card.Body>
          <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
            No messages have been taken down in {selectedAreaName}.
          </p>
        </Card.Body>
      ) : (
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          {visiblePosts.map((post) => (
            <article key={post.id} className="tw-border-t tw-border-line tw-px-4 tw-py-3">
              <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
                {messageBoardTitle(post.message)}
              </h3>
              <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-content-muted">
                Taken down
                {post.deletedAt ? (
                  <>
                    {' '}
                    <time dateTime={post.deletedAt}>{formatMessageBoardTime(post.deletedAt)}</time>
                  </>
                ) : null}
                {post.deletedBy ? ` by ${post.deletedBy.name}` : ''}
              </p>
              <ul className="tw-mb-0 tw-mt-2 tw-list-none tw-pl-0 tw-text-xs tw-text-content-secondary">
                {post.reads.length === 0 ? (
                  <li>No one had read this.</li>
                ) : (
                  post.reads.map((read) => (
                    <li key={read.id}>
                      {read.user.name} · <time dateTime={read.seenAt}>{formatMessageBoardTime(read.seenAt)}</time>
                    </li>
                  ))
                )}
              </ul>
            </article>
          ))}
        </div>
      )}
      {posts && posts.length > MESSAGE_BOARD_PAGE_SIZE && (
        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-line tw-px-4 tw-py-2 tw-text-sm">
          <button
            type="button"
            className="tw-min-h-11 tw-text-content-secondary disabled:tw-opacity-40"
            disabled={currentPage <= 1 || loading}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </button>
          <span className="tw-tabular-nums tw-text-content-muted">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="tw-min-h-11 tw-text-content-secondary disabled:tw-opacity-40"
            disabled={currentPage >= totalPages || loading}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
};

export default MessageBoardAudit;
